import { Component, defineComponent } from "vue";
import { CombinedFieldProps, DataFieldProps } from "../../types";
import { Key } from "../../types/skyward/svg";
import Svg from "../core/svg";
import { DefaultVueProp, VueProp } from "../core/vueHelpers";
import * as Classes from "./classes";

export type CellSlotArguments = {
    nameCell: Component,
    field: CombinedFieldProps,
    parametersCell?: Component | undefined,
    typeIcon: Key,
};

export type ColumnGroup =
{
    header: string;
    class?: string,
    width?: string,
    columns: ((cells: CellSlotArguments) => (Component | null))[];
};

export default /*#__PURE__*/ defineComponent(
{
    name: "treeBrowseSection",
    props:
    {
        showHeader: DefaultVueProp(true),
        columns: VueProp<ColumnGroup[]>(Array),
    },
    render()
    {
        const header =
            <thead>
                {
                    this.columns.map(col =>
                        <th
                            class={[Classes.treebrowseHeaderCell, col.class]}
                            colspan={col.columns.length}
                            style={{
                                width: col.width
                            }}
                        >
                            {col.header}
                        </th>
                    )
                }
            </thead>;

        const table =
            <table class=
                {{
                    [Classes.treebrowseTreeGridTable]: true,
                    [Classes.jsTreebrowseTreeGridTable]: true,
                }}
            >
                {this.showHeader ? header : null}
                <tbody>
                    {this.$slots.default?.()}
                </tbody>
            </table>;
        return table;
    }
});

export function getColumns({ showEyeColumn, anyRowHasShield, anyRowHasDescription, showParametersColumn }: { showEyeColumn: boolean; anyRowHasShield: boolean; anyRowHasDescription: boolean; showParametersColumn: boolean; })
{
    const iconColumn: ColumnGroup = {
        header: "",
        columns: []
    };
    const columns: ColumnGroup[] = [iconColumn];

    if (anyRowHasShield)
    {
        iconColumn.columns.push(({ field }: CellSlotArguments) =>
            <td style={{width: getIconWidth()}}>
                <Svg
                    name="AdvancedSecurity"
                    type="TreeBrowse"
                    style={{ visibility: (field as DataFieldProps).showShield ? "visible" : "hidden" }}
                />
            </td>
        );
    }

    if (showEyeColumn)
    {
        iconColumn.columns.push(
            ({ field }: CellSlotArguments) =>
            {
                const dataField = field as DataFieldProps;
                return (
                    <td style={{ width: getIconWidth() }}>
                        <Svg
                            name={dataField.showEye ? "Eye" : "Hide"}
                            style={{ visibility: ((typeof dataField.showEye) === "boolean") ? "visible" : "hidden" }}
                        />
                    </td>
                );
            }
        );
    }

    if (anyRowHasDescription)
    {
        iconColumn.columns.push(({ field }: CellSlotArguments) =>
        {
            if (field === undefined)
            {
                return (
                    <td style={{ width: getIconWidth() }}>
                        <Svg name="Info" type="TreeBrowse" style={{ visibility: "hidden" }} />;
                    </td>
                );
            }

            return (
                <td style={{ width: getIconWidth() }}>
                    <span> {/* Wrapper for the SVG hover effect */}
                        <Svg name="Info" type="TreeBrowse" style={{ visibility: (field as any).hasDescription ? "visible" : "hidden" }}
                            data-click-action="fieldInformation.click"
                            data-module="SkySys"
                            data-object="Browse"
                            data-screen="FieldInformationPopup"
                            data-field-module={(field as any).moduleName}
                            data-field-object={(field as any).objectName}
                            data-field={(field as any).fieldName}
                        />
                    </span>
                </td>
            );
        });
    }

    iconColumn.columns.push(({ typeIcon }: CellSlotArguments) =>
        <td style={{ width: getIconWidth() }}>
            <Svg name={typeIcon ? typeIcon : "NoImage"} type="TreeBrowse" showHoverEffect={false} style={{ visibility: typeIcon ? "visible" : "hidden" }} />
        </td>
    );

    iconColumn.width = getIconWidth(iconColumn.columns.length);

    columns.push({
        header: "Field",
        columns: [
            ({ nameCell }: CellSlotArguments) => nameCell
        ]
    });

    if (showParametersColumn)
    {
        columns.push({
            header: "Has Parameters",
            width: getIconWidth(),
            columns: [
                ({ parametersCell }: CellSlotArguments) => parametersCell ?? <td />
            ]
        });
    }

    return columns;
}

export function getIconWidth(count: number = 1)
{
    return `${count * 25}px`;
}